import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppTheme } from "@/utils/theme";
import { DrawerMenuPresenter, MenuItem } from "./DrawerMenu.presenter";
import { useUserStore } from "@/hooks/useUserStore";
import { useMenuStore } from "@/hooks/useMenuStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { MODULE_NAME, OPTIONS, ACTION_CODE } from "@/domain/auditlog.enum";

export interface DrawerMenuContainerProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
  appVersion?: {
    functionalVersion?: string;
    technicalVersion?: string;
  };
  onVersionClick?: () => void;
}

/**
 * Container component for DrawerMenu
 * Manages state and business logic
 */
export const DrawerMenuContainer: React.FC<DrawerMenuContainerProps> = ({
  isOpen,
  onClose,
  isMobile = false,
  appVersion,
  onVersionClick,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useUserStore();
  const { menuItems, isLoading } = useMenuStore();
  const theme = useAppTheme();
  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>({});
  const { logout } = useAuth0();
  const createAuditLog = useCreateAuditlog();

  const toggleSubMenu = (menuId: string) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  // Enhanced user info
  const userInfoData = {
    name: `${userInfo?.firstName} ${userInfo?.lastName}`,
    email: userInfo?.email,
    role:
      userInfo?.roles && userInfo.roles.length > 0
        ? userInfo.roles[0].name
        : "",
  };

  const handleLogout = async () => {
    try {
      await createAuditLog.mutateAsync({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.SIGN_OUT,
        option: OPTIONS.SIGN_OUT,
        actionCode: ACTION_CODE.WEB_SIGN_OUT_OK,
        action: "Sign out",
        detail: "User signed out successfully",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
    } finally {
      document.cookie = "";
      sessionStorage.clear();
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  };

  // Transform menuItems to match the expected format (COYN uses 'title', presenter expects 'name')
  const transformedMenuItems: MenuItem[] = (menuItems || []).map(
    (item, index) => ({
      id: item.id || item.title,
      name: item.title, // COYN uses 'title' instead of 'name'
      route: item.route || "",
      path: item.route,
      icon: item.icon,
      order: index,
      children: item.children?.map((child, childIndex) => ({
        id: child.id || child.title,
        name: child.title,
        route: child.route || "",
        path: child.route,
        icon: child.icon,
        order: childIndex,
      })),
    }),
  );

  return (
    <DrawerMenuPresenter
      isOpen={isOpen}
      onClose={onClose}
      menuItems={transformedMenuItems}
      isLoading={isLoading}
      error={null}
      userInfo={userInfoData}
      openSubMenus={openSubMenus}
      onToggleSubMenu={toggleSubMenu}
      onNavigate={handleNavigate}
      currentPath={location.pathname}
      isMobile={isMobile}
      appVersion={appVersion}
      onVersionClick={onVersionClick}
      theme={theme}
      onLogout={handleLogout}
    />
  );
};
